import { useState, useEffect, useMemo, useRef } from "react";
import { Button, Card, CardActions, CardContent, Grid, Stack, useMediaQuery, Typography, CardMedia, Select, MenuItem, TextField, RadioGroup, FormControl, FormControlLabel, Radio, Box, Chip, IconButton, Tooltip } from "@mui/material";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import convoBg from '../../../assests/convoBg.jpg';
import { StepHeader, stepCardSx, stepActionsSx, backBtnSx, nextBtnSx } from "./stepUi";
import { useSelector, useDispatch } from "react-redux";
import { setActiveNext, setActiveBack } from "../campaignSlice";
import { FieldSelect } from "../../../components/selections/fieldInputs";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { setCampaignFlow } from "../campaignSlice";
import PhoneIcon from "@mui/icons-material/Phone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChatIcon from '@mui/icons-material/Chat';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import * as XLSX from 'xlsx';

export default function CampaignCreation() {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery("(max-width:600px)");
    const { activeStep, campaignFlowDetails = [], campaignInputParams = [], tagWiseUsers = [] } = useSelector((state) => state.campaigns);
    const { getTemplates = [] } = useSelector((state) => state.templates);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [state, setState] = useState({
        selectedTemplate: "",
        placeholders: [],
        values: {},
        errors: {},
        selectedPlaceHolderValues: [],
        selectPlaceHolderOptions: {}
    });

    const { selectedTemplate, errors, selectPlaceHolderOptions, placeholders, selectedPlaceHolderValues, values } = state;

    // Uploaded Excel: its header row becomes extra options in the placeholder dropdowns
    const [excelData, setExcelData] = useState(campaignFlowDetails?.[0]?.excelData || null);
    const excelInputRef = useRef(null);
    const excelColumns = excelData?.columns || [];

    // "Body Variable {{1}}" <-> { section: "body", n: "1" }
    const variableLabel = (ph) => `${ph.sectionType === "header" ? "Header" : "Body"} Variable ${ph.placeholder}`;
    const parseVariableHeader = (h) => {
        const m = String(h).match(/^\s*(header|body)?[\s_-]*(?:variable)?[\s_-]*\{\{\s*(\d+)\s*\}\}\s*$/i);
        return m ? { section: (m[1] || "body").toLowerCase(), n: m[2] } : null;
    };

    // From the file picker
    const handleExcelUpload = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        processExcelFile(file);
    };

    // Drag & drop onto the upload panel (works without opening the file window)
    const [isDragging, setIsDragging] = useState(false);
    const handleExcelDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        processExcelFile(e.dataTransfer?.files?.[0]);
    };
    const handleExcelDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragging) setIsDragging(true);
    };

    const processExcelFile = (file) => {
        if (!file) return;

        if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
            setSnackbar({ open: true, message: "Please upload a .xlsx, .xls or .csv file", severity: "error" });
            return;
        }

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const workbook = XLSX.read(evt.target.result, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
                // keep every header in sheet order (duplicates included) for positional mapping
                const headers = (rows[0] || []).map((c) => String(c).trim());
                const fieldRow = (rows[1] || []).map((c) => String(c).trim());

                if (headers.filter(Boolean).length === 0) {
                    setSnackbar({ open: true, message: "No column names found in the first row", severity: "error" });
                    return;
                }

                // Variable format (from the Sample): row 1 "mobile | Body Variable {{1}} | ...",
                // row 2+ one person per row with the value of each variable.
                // Older files had a field-name row under the headers (row 2 with an empty mobile) — still supported.
                const varRefs = headers.map(parseVariableHeader);
                const isVariableFormat = varRefs.some(Boolean);
                const mobileIdx = headers.findIndex((h) => /^(mobile|phone|whatsapp)( ?(no|number))?$/i.test(h));
                const hasFieldRow = isVariableFormat && rows.length > 1
                    && (mobileIdx === -1 || String(rows[1]?.[mobileIdx] ?? "").trim() === "")
                    && fieldRow.some((f, i) => f && varRefs[i]);
                const dataRows = rows.slice(hasFieldRow ? 2 : 1).filter((r) => r.some((c) => String(c).trim() !== ""));

                // key of each variable column: its row-2 field name (old files) or the header itself
                const varKey = (i) => (hasFieldRow ? fieldRow[i] : headers[i]);
                const columns = isVariableFormat
                    ? [...new Set(headers.map((h, i) => (varRefs[i] ? varKey(i) : "")).filter(Boolean))]
                    : [...new Set(headers.filter(Boolean))];
                const data = isVariableFormat
                    ? dataRows.map((r) => {
                        const obj = {};
                        headers.forEach((h, i) => {
                            const key = varRefs[i] ? varKey(i) : h;
                            if (key && !(key in obj)) obj[key] = r[i] ?? "";
                        });
                        return obj;
                    })
                    : XLSX.utils.sheet_to_json(sheet, { defval: "" });
                setExcelData({ fileName: file.name, columns, rows: data });

                // Auto-map placeholders. Variable format: by section + number, using the row-2 field.
                // Plain columns: a "body_1"-style header wins, otherwise columns fill the placeholders in
                // template order, skipping a leading mobile/phone column: mobile | a | b | c -> {{1}}=a {{2}}=b {{3}}=c
                const lower = headers.map((c) => c.toLowerCase());
                const startAt = /^(mobile|phone|whatsapp)( ?(no|number))?$/i.test(headers[0] || "") ? 1 : 0;
                const sequence = headers.slice(startAt).filter(Boolean);
                const nextValues = {};
                const nextOptions = {};
                let mapped = 0;
                let pos = 0;
                placeholders.forEach((ph) => {
                    const n = ph.placeholder.replace(/[{}]/g, "");
                    let column;
                    if (isVariableFormat) {
                        const idx = varRefs.findIndex((r) => r && r.section === ph.sectionType && r.n === n);
                        // map only when the column has at least one value, else leave the dropdown for a contact field
                        const hasValues = idx !== -1 && (hasFieldRow || dataRows.some((r) => String(r[idx] ?? "").trim() !== ""));
                        column = hasValues ? varKey(idx) : "";
                    } else {
                        const byName = lower.indexOf(`${ph.sectionType}_${n}`);
                        column = byName !== -1 ? headers[byName] : sequence[pos++];
                    }
                    if (!column) return;
                    const key = `${ph.sectionType}_${ph.placeholder}_${ph.sectionIndex}`;
                    nextValues[key] = { sectionType: ph.sectionType, type: "select", value: column };
                    nextOptions[key] = "1";
                    mapped++;
                });
                setState((prev) => ({
                    ...prev,
                    values: { ...prev.values, ...nextValues },
                    selectPlaceHolderOptions: { ...prev.selectPlaceHolderOptions, ...nextOptions },
                    errors: Object.fromEntries(Object.entries(prev.errors).filter(([k]) => !nextValues[k])),
                }));

                setSnackbar({
                    open: true,
                    message: `${columns.length} fields loaded · ${mapped} of ${placeholders.length} placeholders mapped`,
                    severity: mapped === placeholders.length ? "success" : "warning",
                });
            } catch {
                setSnackbar({ open: true, message: "Unable to read the Excel file", severity: "error" });
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleRemoveExcel = () => {
        // clear any placeholder that was mapped to an Excel column
        setState((prev) => {
            const nextValues = { ...prev.values };
            Object.keys(nextValues).forEach((k) => {
                if (nextValues[k]?.type === "select" && excelColumns.includes(nextValues[k].value)) {
                    nextValues[k] = { ...nextValues[k], value: "" };
                }
            });
            return { ...prev, values: nextValues };
        });
        setExcelData(null);
    };

    // Sample sheet built from the selected template: row 1 = "mobile | Body Variable {{1}} | ...",
    // row 2+ = one person per row (contacts of the tags chosen in the Audience step).
    // Each variable is prefilled from what is chosen now: a contact field -> that person's value
    // (name, mobile ...), a static value -> the text; anything else is left blank to fill in.
    const handleDownloadSample = () => {
        const headers = ["mobile", ...placeholders.map(variableLabel)];
        const choices = placeholders.map((ph) => values[`${ph.sectionType}_${ph.placeholder}_${ph.sectionIndex}`] || {});
        const users = Array.isArray(tagWiseUsers) ? tagWiseUsers : [];
        const contactRows = users
            .filter((u) => u?.mobile)
            .map((u) => [
                String(u.mobile), // text, so Excel does not show 9.17E+11
                ...choices.map((c) => {
                    if (!c.value) return "";
                    if (c.type === "input") return String(c.value);
                    return u[c.value] != null ? String(u[c.value]) : "";
                }),
            ]);
        const ws = XLSX.utils.aoa_to_sheet([headers, ...contactRows]);
        ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length, 16) + 2 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        const name = getTemplates.find((t) => t.id === selectedTemplate)?.template_name || "template";
        XLSX.writeFile(wb, `${name}_sample.xlsx`);
    };


    const handleSingleSelectChange = (name, selectedValue) => {
        dispatch(setCampaignFlow([]));
        setExcelData(null);
        setState((prev) => ({
            ...prev,
            [name]: selectedValue,
            placeholders: [],
            values: {},
            selectedPlaceHolderValues: [],
            errors: { ...prev.errors, [name]: "" },
        }));
    };

    const formatText = (text) => {
        if (!text || typeof text !== 'string') {
            return '';
        }
        return text.replace(/\*([^*]+)\*/g, '<strong>$1</strong>').replace(/\\n/g, '\n')
    };

    // Preview: swap each {{n}} for the field / static text chosen for it (falls back to {{n}})
    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const fillPlaceholders = (text, sectionType, sectionIndex) => {
        if (!text || typeof text !== "string") return text;
        return text.replace(/\{\{\d+\}\}/g, (ph) => {
            const val = values[`${sectionType}_${ph}_${sectionIndex}`];
            if (!val?.value) return ph;
            const isField = val.type === "select";
            const isExcel = isField && excelColumns.includes(val.value);
            // Show the variable number ({{1}}), highlighted once it is mapped; the mapped field / text is on hover
            const tone = isExcel
                ? { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" }
                : isField
                    ? { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" }
                    : { bg: "#fefce8", color: "#a16207", border: "#fde68a" };
            const style = `display:inline-block;padding:0 6px;border-radius:6px;font-family:monospace;font-size:0.9em;font-weight:600;line-height:1.5;background:${tone.bg};color:${tone.color};border:1px solid ${tone.border}`;
            const hint = isExcel ? "Excel" : isField ? `Field: ${val.value}` : `Text: ${val.value}`;
            return `<span title="${escapeHtml(hint)}" style="${style}">${ph}</span>`;
        });
    };

    const matchedTemplate =getTemplates.find((item) => item.id === selectedTemplate);

    const template_form = useMemo(() => {
        return matchedTemplate?.template_form || [];
    }, [matchedTemplate]);


    useEffect(() => {
        const phs = [];
        template_form.forEach((section, sectionIndex) => {
            const type = section.type?.toLowerCase();
            if (!["header", "body"].includes(type)) return;

            const matches = section.text?.match(/\{\{\d+\}\}/g) || [];
            matches.forEach((ph) => {
                if (!phs.find((p) => p.placeholder === ph && p.sectionType === type)) {
                    phs.push({
                        placeholder: ph,
                        sectionType: type,
                        sectionIndex: sectionIndex + 1
                    });
                }
            });
        });
        setState(prev => ({ ...prev, placeholders: phs, values: {}, errors: {} }));
    }, [template_form]);

    const handlePlaceholderChange = (placeholder, sectionType, inputType, value, sectionIndex) => {
        const key = `${sectionType}_${placeholder}_${sectionIndex}`;

        setState(prev => ({
            ...prev,
            values: {
                ...prev.values,
                [key]: { sectionType, type: inputType, value }
            },
            errors: {
                ...prev.errors,
                [key]: ""
            },
        }));
    };

    useEffect(() => {
        const selected = Object.entries(values)
            .filter(([_, val]) => val.value)
            .map(([placeholder, val]) => ({
                type: val.sectionType === "header" ? "head" : "body",
                placeholder,
                value: val.value,
                inputType: val.type
            }));
        setState(prev => ({ ...prev, selectedPlaceHolderValues: selected }));
    }, [values]);


    // Validation
    const handleSubmitCampaignFlow = () => {
        const newErrors = {};

        if (!selectedTemplate && selectedTemplate === "") {
            newErrors.selectedTemplate = { error: "Template is required" };
            setSnackbar({ open: true, message: "Template is required", severity: "error" });
            setState(prev => ({ ...prev, errors: newErrors }));
            return false;
        }

        placeholders.forEach(ph => {
            const key = `${ph.sectionType}_${ph.placeholder}_${ph.sectionIndex}`;
            const val = values[key]?.value;
            const type = values[key]?.type; // "select" or "input"

            if (type === "select") {
                if (!val && val !== 0) { // allow 0 as valid
                    newErrors[key] = { error: `Please select value for ${ph.sectionType} ${ph.placeholder}` };
                }
            } else { // input
                if (!val || val.toString().trim() === "") {
                    newErrors[key] = { error: `Please fill value for ${ph.sectionType} ${ph.placeholder}` };
                }
            }
        });


        setState(prev => ({ ...prev, errors: newErrors }));

        if (Object.keys(newErrors).length > 0) {
            setSnackbar({ open: true, message: "Please fill all required fields.", severity: "error" });
            return false;
        }

        setSnackbar({ open: true, message: "All fields are valid!", severity: "success" });
        return true;
    };


    const handleNext = () => {
        const isValid = handleSubmitCampaignFlow();
        if (!isValid) return;

        dispatch(setCampaignFlow([{
            selectedTemplate: selectedTemplate,
            placeholders: placeholders,
            values: values,
            selectedPlaceHolderValues: selectedPlaceHolderValues,
            selectPlaceHolderOptions: state.selectPlaceHolderOptions,
            excelData: excelData,
        }]));
        dispatch(setActiveNext());
    };


    const handleBack = () => {
        dispatch(setCampaignFlow([{
            selectedTemplate: state.selectedTemplate,
            placeholders: state.placeholders,
            values: state.values,
            selectedPlaceHolderValues: state.selectedPlaceHolderValues,
            selectPlaceHolderOptions: state.selectPlaceHolderOptions,
            excelData: excelData,

        }]));
        dispatch(setActiveBack());
    };

    useEffect(() => {
        const details = campaignFlowDetails?.[0];
        if (!details) return;

        const { selectedTemplate, placeholders, values, selectedPlaceHolderValues, selectPlaceHolderOptions } = details;

        setState(prev => ({
            ...prev,
            selectedTemplate: selectedTemplate ?? prev.selectedTemplate,
            placeholders: placeholders?.length ? placeholders : prev.placeholders,
            values: Object.keys(values || {}).length ? values : prev.values,
            selectedPlaceHolderValues: Array.isArray(selectedPlaceHolderValues) && selectedPlaceHolderValues.length > 0
                ? selectedPlaceHolderValues
                : prev.selectedPlaceHolderValues,
            selectPlaceHolderOptions: selectPlaceHolderOptions,
        }));
    }, [JSON.stringify(campaignFlowDetails), template_form]);




    useEffect(() => {
        // Initialize selectPlaceHolderOptions with "1" for all placeholders
        const defaultSelectOptions = {};
        placeholders.forEach(ph => {
            const key = `${ph.sectionType}_${ph.placeholder}_${ph.sectionIndex}`;
            defaultSelectOptions[key] = "1"; // default = Dynamic
        });

        setState(prev => ({
            ...prev,
            selectPlaceHolderOptions: {
                ...defaultSelectOptions,
                ...prev.selectPlaceHolderOptions, // merge existing saved values if any
            }
        }));
    }, [placeholders]);


    return (
        <div>
            <Card sx={stepCardSx}>
                <CardContent sx={{ maxHeight: "60vh", overflowY: "auto", bgcolor: "background.paper", scrollbarWidth: "thin", p: { xs: 2, sm: 3 } }}>
                    <StepHeader icon={<ChatIcon sx={{ fontSize: 20 }} />} title="Message" subtitle="Pick an approved template and fill in its variables" />
                    <Grid container spacing={3}>
                        <Grid item size={{ xs: 12, md: 6 }}>
                            <FieldSelect
                                name="selectedTemplate"
                                label="Template"
                                required
                                icon={<DescriptionOutlinedIcon />}
                                placeholder="Select an approved template"
                                options={getTemplates.filter((item1) => item1.status.toLowerCase() === "approved").map((item) => ({ value: item.id, label: item.template_name }))}
                                value={selectedTemplate}
                                onChange={handleSingleSelectChange}
                                error={!!errors.selectedTemplate}
                            />


                            {selectedTemplate !== "" && <Grid container spacing={1} mt={3}>

                                {placeholders.length > 0 ? (
                                    <>
                                        {/* Excel upload — column names become dropdown options */}
                                        <Grid item size={{ xs: 12 }}>
                                            <Box
                                                onDragOver={handleExcelDragOver}
                                                onDragEnter={handleExcelDragOver}
                                                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                                                onDrop={handleExcelDrop}
                                                sx={{ mb: 1.5, p: { xs: 1.5, sm: 2 }, borderRadius: 3, border: "1.5px dashed", transition: "all .15s", borderColor: isDragging ? "#2563eb" : excelData ? "#bfdbfe" : "#cbd5e1", bgcolor: isDragging ? "#eff6ff" : excelData ? "#f5f9ff" : "#fff" }}
                                            >
                                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                                                        <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "#ecfdf5", color: "#059669" }}>
                                                            <TableChartOutlinedIcon sx={{ fontSize: 22 }} />
                                                        </Box>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }} noWrap>
                                                                {isDragging ? "Drop the Excel file here" : excelData ? excelData.fileName : "Upload Excel — drag & drop or click Upload"}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
                                                                {excelData
                                                                    ? `${excelData.columns.length} fields · ${excelData.rows.length} rows — mapped to the placeholders below, change any dropdown to remap`
                                                                    : "Row 1: mobile, Body Variable {{1}}, {{2}}… · Row 2 onwards: one person per row with their values — download the Sample"}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                                                        <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={handleDownloadSample} sx={{ textTransform: "none", fontWeight: 600, color: "#475569", borderRadius: 2 }}>
                                                            Sample
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            size="small"
                                                            variant={excelData ? "outlined" : "contained"}
                                                            startIcon={<UploadFileRoundedIcon />}
                                                            disableElevation
                                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); excelInputRef.current?.click(); }}
                                                            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                                                        >
                                                            {excelData ? "Replace" : "Upload"}
                                                        </Button>
                                                        {/* kept outside the button so one click opens the picker exactly once */}
                                                        <input
                                                            ref={excelInputRef}
                                                            type="file"
                                                            accept=".xlsx,.xls,.csv"
                                                            onChange={handleExcelUpload}
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ display: "none" }}
                                                        />
                                                        {excelData && (
                                                            <Tooltip title="Remove file">
                                                                <IconButton size="small" onClick={handleRemoveExcel} sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" } }}>
                                                                    <CloseRoundedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </Stack>
                                                {excelData && (
                                                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
                                                        {excelColumns.map((col) => (
                                                            <Chip key={col} label={col} size="small" sx={{ fontFamily: "monospace", fontSize: 12, bgcolor: "#fff", border: "1px solid #dbeafe", color: "#1d4ed8" }} />
                                                        ))}
                                                    </Stack>
                                                )}
                                            </Box>
                                        </Grid>

                                        {placeholders.map((phItem) => {
                                            const key = `${phItem.sectionType}_${phItem.placeholder}_${phItem.sectionIndex}`;
                                            const sectionKey = phItem.sectionType;
                                            const isDynamic = selectPlaceHolderOptions[key] === "1";
                                            const valueObj = values[key] || {};
                                            const error = !!errors[key];

                                            return (
                                                <Grid item size={{ xs: 12, md: 12 }} key={key}>
                                                    <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 3, borderColor: "#e9edf2", bgcolor: "#f8fafc" }}>
                                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: "#0f172a", letterSpacing: 0.3 }}>
                                                                {phItem.sectionType.toUpperCase()} - Section {phItem.placeholder.replace(/{{(\d+)}}/g, '$1')}
                                                            </Typography>

                                                            {/* Radio buttons */}
                                                            <FormControl component="fieldset" sx={{ mb: 1.5 }}>
                                                                <RadioGroup
                                                                    row
                                                                    sx={{
                                                                        display: "inline-flex",
                                                                        p: 0.5,
                                                                        gap: 0.5,
                                                                        bgcolor: "#eef2f6",
                                                                        borderRadius: 5,
                                                                        width: "fit-content",
                                                                        // each option is a pill; the checked one is solid blue
                                                                        "& .MuiFormControlLabel-root": {
                                                                            m: 0,
                                                                            pl: 0.5,
                                                                            pr: 1.75,
                                                                            py: 0.25,
                                                                            borderRadius: 5,
                                                                            transition: "all .15s ease",
                                                                            "& .MuiTypography-root": { fontSize: 13.5, fontWeight: 700, color: "#475569" },
                                                                            "& .MuiRadio-root": { p: 0.5, color: "#94a3b8", "& svg": { fontSize: 18 } },
                                                                        },
                                                                        "& .MuiFormControlLabel-root:has(.Mui-checked)": {
                                                                            bgcolor: "#2563eb",
                                                                            boxShadow: "0 4px 10px -4px rgba(37,99,235,0.6)",
                                                                            "& .MuiTypography-root": { color: "#fff" },
                                                                            "& .MuiRadio-root": { color: "#fff" },
                                                                        },
                                                                    }}
                                                                    name={`${sectionKey}_${phItem.placeholder}_${phItem.sectionIndex}_radio`}
                                                                    value={selectPlaceHolderOptions[key] || "1"}
                                                                    onChange={(e) => {
                                                                        const newValue = e.target.value;
                                                                        setState((prev) => ({
                                                                            ...prev,
                                                                            selectPlaceHolderOptions: {
                                                                                ...prev.selectPlaceHolderOptions,
                                                                                [key]: newValue,
                                                                            },
                                                                            values: {
                                                                                ...prev.values,
                                                                                [key]: {
                                                                                    ...prev.values[key],
                                                                                    type: newValue === "1" ? "select" : "input",
                                                                                    value: "",
                                                                                }
                                                                            }
                                                                        }));
                                                                    }}
                                                                >
                                                                    <FormControlLabel value="1" control={<Radio />} label="Dynamic Value" />
                                                                    <FormControlLabel value="2" control={<Radio />} label="Static Value" />
                                                                </RadioGroup>
                                                            </FormControl>

                                                            {/* Input / Select */}
                                                            <Grid container spacing={1.5} alignItems="center">
                                                                <Grid item size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: "left", md: "center" } }}>
                                                                    <Box component="span" sx={{ display: "inline-block", mt: 0, px: 1, py: 0.25, borderRadius: 1.5, fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: "#2563eb", bgcolor: "#eff6ff", border: "1px solid #bfdbfe" }}>{phItem.placeholder}</Box>
                                                                </Grid>
                                                                <Grid item size={{ xs: 12, md: 10 }}>
                                                                    {isDynamic ? (
                                                                        <FormControl fullWidth size="small" error={error}>
                                                                            <Select
                                                                                value={valueObj.type === "select" ? valueObj.value : ""}
                                                                                displayEmpty
                                                                                IconComponent={KeyboardArrowDownRoundedIcon}
                                                                                startAdornment={<DataObjectRoundedIcon sx={{ fontSize: 19, mr: 1, color: valueObj.type === "select" && valueObj.value ? "#2563eb" : "#9ca3af" }} />}
                                                                                renderValue={(v) => !v
                                                                                    ? <Box component="span" sx={{ color: "#9ca3af" }}>Select a variable</Box>
                                                                                    : excelColumns.includes(v) && !campaignInputParams?.some((item) => item.template_variable === v)
                                                                                        ? <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: "#047857", fontWeight: 600 }}><TableChartOutlinedIcon sx={{ fontSize: 16 }} />From Excel</Box>
                                                                                        : v}
                                                                                sx={{
                                                                                height: 42,
                                                                                borderRadius: 2.5,
                                                                                bgcolor: "#fff",
                                                                                fontSize: 14,
                                                                                "& .MuiSelect-icon": { color: "#64748b", right: 10 },
                                                                                "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: error ? "#ef4444" : "#e5e7eb" },
                                                                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: error ? "#ef4444" : "#cbd5e1" },
                                                                                "&.Mui-focused": { boxShadow: error ? "0 0 0 3px rgba(239,68,68,0.12)" : "0 0 0 3px rgba(37,99,235,0.12)" },
                                                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: error ? "#ef4444" : "#2563eb" },
                                                                            }}
                                                                                MenuProps={{ PaperProps: { sx: { mt: 0.5, maxHeight: 280, borderRadius: 2.5, border: "1px solid #e5e7eb", boxShadow: "0 12px 28px -12px rgba(15,23,42,0.3)", "& .MuiMenuItem-root": { fontSize: 13.5, fontFamily: "monospace", mx: 0.75, my: 0.25, borderRadius: 1.5 }, "& .MuiMenuItem-root:hover": { bgcolor: "#f8fafc" }, "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": { bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 600 } } } }}
                                                                                onChange={(e) =>
                                                                                    handlePlaceholderChange(
                                                                                        phItem.placeholder,
                                                                                        phItem.sectionType,
                                                                                        "select",
                                                                                        e.target.value,
                                                                                        phItem.sectionIndex
                                                                                    )
                                                                                }
                                                                            >
                                                                                {/* Excel fields are not listed; a variable mapped from the Excel keeps its value
                                                                                    through a hidden item (avoids MUI's out-of-range value warning) */}
                                                                                {excelColumns
                                                                                    .filter((col) => col === valueObj.value && !campaignInputParams?.some((item) => item.template_variable === col))
                                                                                    .map((col) => (
                                                                                        <MenuItem key={`excel_${col}`} value={col} sx={{ display: "none" }}>
                                                                                            {col}
                                                                                        </MenuItem>
                                                                                    ))}
                                                                                {campaignInputParams?.map((item) => (
                                                                                    <MenuItem key={item.id} value={item.template_variable}>
                                                                                        {item.template_variable}
                                                                                    </MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                        </FormControl>
                                                                    ) : (
                                                                        <TextField
                                                                            fullWidth
                                                                            size="small"
                                                                            placeholder={`Enter ${phItem.sectionType} ${phItem.placeholder}`}
                                                                            value={valueObj.type === "input" ? valueObj.value : ""}
                                                                            error={error}
                                                                            inputProps={{ maxLength: 20 }}
                                                                            sx={{ "& .MuiOutlinedInput-root": {
                                                                                height: 42,
                                                                                borderRadius: 2.5,
                                                                                bgcolor: "#fff",
                                                                                fontSize: 14,
                                                                                "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: error ? "#ef4444" : "#e5e7eb" },
                                                                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: error ? "#ef4444" : "#cbd5e1" },
                                                                                "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(37,99,235,0.14)" },
                                                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: error ? "#ef4444" : "#2563eb" },
                                                                            }, "& input::placeholder": { color: "#9ca3af", opacity: 1 } }}
                                                                            onChange={(e) =>
                                                                                handlePlaceholderChange(
                                                                                    phItem.placeholder,
                                                                                    phItem.sectionType,
                                                                                    "input",
                                                                                    e.target.value,
                                                                                    phItem.sectionIndex
                                                                                )
                                                                            }
                                                                        />
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            );
                                        })}

                                    </>
                                ) : (
                                    <Typography sx={{ textAlign: "center", py: 2 }}>
                                        {/* No placeholders found */}
                                    </Typography>
                                )}

                            </Grid>}
                        </Grid>

                        {selectedTemplate !== "" && <Grid item size={{ xs: 12, md: 6 }} >
                            <Typography variant="caption" sx={{ display: "block", mb: 0.9, fontSize: 13, fontWeight: 500, letterSpacing: 0.6, textTransform: "uppercase", color: "#4b5563" }}>
                                Preview
                            </Typography>
                            <Box sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, bgcolor: "#efeae2", backgroundImage: `url(${convoBg})`, backgroundRepeat: "repeat", backgroundSize: "340px auto", border: "1px solid #e9edf2" }}>
                            <Card sx={{ width: isMobile ? "100%" : "85%", ml: "auto", overflowWrap: "anywhere", borderRadius: "12px 4px 12px 12px", bgcolor: "#d9fdd3", boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)" }}>
                                {Array.isArray(template_form) && template_form.length > 0 ? (
                                    template_form.map((item, index) => {
                                        const type = item.type?.toLowerCase();
                                        const format = item.format?.toLowerCase();
                                        const headerHandle = item.example?.header_handle?.[0];

                                        return (
                                            <>
                                                {type === "header" && format === "text" && (
                                                    <CardContent>
                                                        <Typography
                                                            variant="h6"
                                                            fontWeight="bold"
                                                            color="text.primary"
                                                            whiteSpace="pre-line"
                                                            dangerouslySetInnerHTML={{ __html: formatText(fillPlaceholders(item.text, "header", index + 1)) }}
                                                        />
                                                    </CardContent>
                                                )}

                                                {type === "header" && format === "image" && headerHandle && (
                                                    <CardMedia
                                                        component="img"
                                                        image={headerHandle}
                                                        alt="Header Image"
                                                        sx={{
                                                            height: { xs: 200, sm: 300 },
                                                            width: "100%",
                                                            objectFit: "contain",
                                                            borderRadius: "8px",
                                                            border: "1px solid var(--disabled-color)",
                                                        }}
                                                    />
                                                )}

                                                {type === "header" && format === "video" && headerHandle && (
                                                    <CardMedia component="video" controls sx={{
                                                        height: { xs: 200, sm: 300 },
                                                        width: "100%",
                                                        objectFit: "contain",
                                                        borderRadius: "8px",
                                                        border: "1px solid var(--disabled-color)",
                                                    }}>
                                                        <source src={headerHandle} type="video/mp4" />
                                                    </CardMedia>
                                                )}

                                                {type === "header" && format === "document" && headerHandle && (
                                                    <CardContent sx={{ textAlign: "center" }}>
                                                        <a href={headerHandle} target="_blank" rel="noopener noreferrer">
                                                            View Document
                                                        </a>
                                                    </CardContent>
                                                )}

                                                {/* ===== Body Section ===== */}
                                                {type === "body" && (
                                                    <CardContent>
                                                        <Typography
                                                            variant="body1"
                                                            color="text.primary"
                                                            whiteSpace="pre-line"
                                                            dangerouslySetInnerHTML={{ __html: formatText(fillPlaceholders(item.text, "body", index + 1)) }}
                                                        />
                                                    </CardContent>
                                                )}

                                                {/* ===== Footer Section ===== */}
                                                {type === "footer" && (
                                                    <CardContent>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            dangerouslySetInnerHTML={{ __html: formatText(item.text) }}
                                                        />
                                                    </CardContent>
                                                )}

                                                {/* ===== Buttons Section ===== */}

                                                {type === "buttons" && item.buttons?.length > 0 && (
                                                    <Grid container spacing={1} sx={{ p: 1 }}>
                                                        {item.buttons.map((button, btnIndex) => (
                                                            <Grid
                                                                item
                                                                key={btnIndex}
                                                                size={{ xs: 12, md: item.buttons.length > 3 ? 6 : 12 }}
                                                            >
                                                                {button.type === "url" && (
                                                                    <Button
                                                                        fullWidth
                                                                        variant="outlined"
                                                                        href={button.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        startIcon={<OpenInNewIcon />}
                                                                    >
                                                                        {button.text}
                                                                    </Button>
                                                                )}

                                                                {button.type === "PHONE_NUMBER" && (
                                                                    <Button
                                                                        fullWidth
                                                                        variant="outlined"
                                                                        href={`tel:${button.phone_number}`}
                                                                        sx={{ fontSize: item.buttons.length > 3 ? "12px" : "" }}
                                                                        startIcon={<PhoneIcon />}
                                                                    >
                                                                        {button.text}
                                                                    </Button>
                                                                )}

                                                                {button.type === "QUICK_REPLY" && (
                                                                    <Button
                                                                        fullWidth
                                                                        variant="contained"
                                                                        color="primary"
                                                                        startIcon={<ChatIcon />}
                                                                    >
                                                                        {button.text}
                                                                    </Button>
                                                                )}
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                )}

                                            </>
                                        );
                                    })
                                ) : (
                                    <Typography sx={{ textAlign: "center", py: 2 }}>No template form data</Typography>
                                )}
                            </Card>
                            </Box>
                        </Grid>}
                    </Grid>
                </CardContent>

                <CardActions sx={stepActionsSx}>
                    <Stack direction="row" spacing={1.5}>
                        <Button disabled={activeStep === 0} variant="outlined" onClick={handleBack} sx={backBtnSx}>
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            disabled={activeStep >= 3}
                            sx={nextBtnSx}
                        >
                            Next
                        </Button>
                    </Stack>
                </CardActions>
            </Card>

            <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />
        </div>
    );
}